// src/utils/formatCurrency.js

/**
 * Format a numeric string as currency while preserving the number of decimals
 * present in the input.
 *
 * Defaults (Option B): locale 'en-US', currency 'USD'
 *
 * @param {string|null|undefined} valueString - numeric value as a string (e.g. "1234", "1234.1", "12345.12")
 * @param {Object} [options]
 * @param {string} [options.locale='en-US']
 * @param {string} [options.currency='USD']
 * @param {string} [options.emptyDisplay='-']
 * @returns {string} formatted currency or emptyDisplay
 */

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY = 'USD';
const DEFAULT_EMPTY_DISPLAY = '-';

const formatterCache = new Map();

function getFormatter(locale, currency, fractionDigits) {
    const key = `${locale}|${currency}|${fractionDigits}`;
    if (formatterCache.has(key)) return formatterCache.get(key);

    const fmt = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
    });

    if (formatterCache.size >= 50 && !formatterCache.has(key)) {
        formatterCache.clear();
    }
    formatterCache.set(key, fmt);
    return fmt;
}

export default function formatCurrencyString(
    valueString,
    {
        locale = DEFAULT_LOCALE,
        currency = DEFAULT_CURRENCY,
        emptyDisplay = DEFAULT_EMPTY_DISPLAY,
    } = {},
) {
    if (valueString === null || valueString === undefined) return emptyDisplay;

    const raw = String(valueString).trim();
    if (raw === '') return emptyDisplay;

    const normalized = raw.replace(/,/g, '').replace(/\s+/g, '');

    const hasTrailingDot = normalized.endsWith('.');

    const numericRegex = /^-?\d+(\.\d+)?$/;
    if (!numericRegex.test(normalized) && !hasTrailingDot) {
        return emptyDisplay;
    }

    let fractionDigits = 0;
    if (hasTrailingDot) {
        fractionDigits = 0;
    } else if (normalized.includes('.')) {
        fractionDigits = normalized.split('.')[1].length;
    } else {
        fractionDigits = 0;
    }

    const number = Number(normalized);
    if (!Number.isFinite(number)) {
        return emptyDisplay;
    }

    const safeFractionDigits = Math.min(Math.max(0, fractionDigits), 20);

    const finalFractionDigits = safeFractionDigits > 0 ? 2 : 0;

    try {
        const formatter = getFormatter(locale, currency, finalFractionDigits);
        return formatter.format(number);
    } catch (err) {
        return emptyDisplay;
    }
}

/**
 * Format a numeric string as currency, always displaying 2 decimal places.
 *
 * Unlike `formatCurrencyString` (which preserves the input's decimal count),
 * this variant forces 2 decimals so whole numbers render as `$999.00`.
 *
 * Non-numeric input returns `emptyDisplay`.
 *
 * @param {string|null|undefined} valueString - numeric value as a string
 * @param {Object} [options]
 * @param {string} [options.locale='en-US']
 * @param {string} [options.currency='USD']
 * @param {string} [options.emptyDisplay='-']
 * @returns {string} formatted currency with 2 decimals or emptyDisplay
 */
export function formatCurrencyStringV2(
    valueString,
    {
        locale = DEFAULT_LOCALE,
        currency = DEFAULT_CURRENCY,
        emptyDisplay = DEFAULT_EMPTY_DISPLAY,
    } = {},
) {
    if (valueString === null || valueString === undefined) return emptyDisplay;

    const raw = String(valueString).trim();
    if (raw === '') return emptyDisplay;

    const normalized = raw.replace(/,/g, '').replace(/\s+/g, '');

    const numericRegex = /^-?\d+(\.\d+)?$/;
    if (!numericRegex.test(normalized)) {
        return emptyDisplay;
    }

    const number = Number(normalized);
    if (!Number.isFinite(number)) {
        return emptyDisplay;
    }

    try {
        const formatter = getFormatter(locale, currency, 2);
        return formatter.format(number);
    } catch (err) {
        return emptyDisplay;
    }
}
